import { useMemo } from 'react'
import {
  useMyLocationCoordinates,
  type MyLocationCoordinates
} from './useMyLocationCoordinates'
import { useGeolocation } from '../../../../../utils/geo-location'

export interface UseStoreLocationOptions {
  /**
   * Coordinates parsed from URL search params (e.g. StoreMap `?lat=&lng=`).
   * When both values are present and numeric, they take highest priority.
   */
  urlCoords?: {
    latitude?: number | string
    longitude?: number | string
  }
  /**
   * When true, fall back to IP-based (Akamai Edgescape) geolocation if neither
   * URL coords nor "My Location" produce usable coordinates. StoreMap sets this
   * to false because it must never use IP geolocation.
   */
  enableIpFallback?: boolean
}

export interface StoreLocation {
  latitude: number | undefined
  longitude: number | undefined
  isLoading: boolean
  error: Error | undefined
  isAuthor: boolean
}

/**
 * Parses a coordinate value (string | number | undefined) into a finite number,
 * or undefined when it is missing / not a usable number. `0` is treated as a
 * valid coordinate; only NaN / null / undefined / empty are rejected.
 */
const toCoord = (value: number | string | undefined): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/** True when both coordinates resolved to usable, truthy numbers. */
const hasUsableCoords = (
  latitude: number | undefined,
  longitude: number | undefined
): boolean => Boolean(latitude) && Boolean(longitude)

/**
 * Resolves the effective coordinates for the Store Locator components
 * (NearByStores, StoreMap) from a single, shared priority chain — see
 * DCXWAM-41590.
 *
 * Priority (first settled source with usable numeric coords wins):
 *   1. `urlCoords` — when both lat & lng are valid numbers (user searched).
 *   2. "My Location" (us_loc cookie, US market) — when the widget is available,
 *      settled (not loading), error-free, and has coordinates.
 *   3. IP-based geolocation (Akamai Edgescape) — ONLY when `enableIpFallback`
 *      is true and My Location is unavailable / errored / empty after loading.
 *
 * Loading: while the currently-relevant source is still resolving we report
 * `isLoading: true` and do NOT fall through prematurely (so a consumer never
 * fetches against stale/empty coords while My Location is still geocoding).
 *
 * Error: only the error of the source actually used/settled-on is surfaced. A
 * non-chosen source's error is suppressed, so (for example) a My Location
 * geocoding error can never wedge a consumer that has an IP fallback available.
 *
 * The source hooks are always called unconditionally to respect the rules of
 * hooks; the `enableIpFallback` / `urlCoords` flags only affect selection.
 */
export const useStoreLocation = (
  options: UseStoreLocationOptions = {}
): StoreLocation => {
  const { urlCoords, enableIpFallback = false } = options

  const myLocation: MyLocationCoordinates = useMyLocationCoordinates()
  const geolocation = useGeolocation()

  const urlLatitude = toCoord(urlCoords?.latitude)
  const urlLongitude = toCoord(urlCoords?.longitude)

  const myLat = toCoord(myLocation.latitude)
  const myLng = toCoord(myLocation.longitude)

  const geoLat = toCoord(geolocation.latitude)
  const geoLng = toCoord(geolocation.longitude)

  return useMemo<StoreLocation>(() => {
    const isAuthor = Boolean(geolocation.isAuthor)

    const ipFallback: StoreLocation = enableIpFallback
      ? {
          latitude: geoLat,
          longitude: geoLng,
          isLoading: geolocation.isLoading,
          error: geolocation.error,
          isAuthor
        }
      : {
          latitude: undefined,
          longitude: undefined,
          isLoading: false,
          error: undefined,
          isAuthor
        }

    // 1. URL coords take precedence when both are usable numbers.
    if (hasUsableCoords(urlLatitude, urlLongitude)) {
      return {
        latitude: urlLatitude,
        longitude: urlLongitude,
        isLoading: false,
        error: undefined,
        isAuthor
      }
    }

    // 2. My Location (US market) — only when the widget is available.
    if (myLocation.isMyLocationAvailable) {
      // Still resolving — stay loading, don't fall through yet.
      if (myLocation.isLoading) {
        return {
          latitude: undefined,
          longitude: undefined,
          isLoading: true,
          error: undefined,
          isAuthor
        }
      }

      // Settled with usable coords and no error — prefer it.
      if (!myLocation.error && hasUsableCoords(myLat, myLng)) {
        return {
          latitude: myLat,
          longitude: myLng,
          isLoading: false,
          error: undefined,
          isAuthor
        }
      }

      // Settled but errored / empty — fall back to IP source (its error, not
      // My Location's, is what gets surfaced).
      return ipFallback
    }

    // 3. My Location unavailable (non-US) — use the IP fallback source.
    return ipFallback
  }, [
    urlLatitude,
    urlLongitude,
    myLocation.isMyLocationAvailable,
    myLocation.isLoading,
    myLocation.error,
    myLat,
    myLng,
    enableIpFallback,
    geoLat,
    geoLng,
    geolocation.isLoading,
    geolocation.error,
    geolocation.isAuthor
  ])
}
